import React, { useEffect, useRef, useState } from 'react';

interface SituationCard {
  tag: string;
  title: string;
  description: string;
  highlight: boolean;
}

interface SituationGuideProps {
  cards: SituationCard[];
}

export default function SituationGuide({ cards }: SituationGuideProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="px-5 py-10 md:px-8 md:py-14">
      <div
        className={`mx-auto max-w-[480px] transition-all duration-700 md:max-w-[700px] ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}
      >
        <div className="mb-6 flex items-center gap-2">
          <div className="h-px w-6 bg-softgold/40" />
          <span className="text-[11px] font-medium tracking-wider text-softgold">
            상황별 선택 가이드
          </span>
        </div>

        <h2 className="mb-7 text-[18px] font-semibold tracking-tight text-navy md:text-[20px]">
          상황에 따라 이렇게 선택해보세요
        </h2>

        <div className="space-y-3 md:grid md:grid-cols-3 md:gap-3 md:space-y-0">
          {cards.map((card, i) => (
            <div
              key={i}
              className={`rounded-xl border px-5 py-5 transition-all duration-500 ${
                card.highlight
                  ? 'border-softgold/30 bg-navy text-cream'
                  : 'border-navy/8 bg-cream-light text-navy'
              } ${
                visible
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-3'
              }`}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <span
                className={`mb-3 inline-block rounded-full px-2.5 py-0.5 text-[10px] font-medium tracking-wide ${
                  card.highlight
                    ? 'bg-softgold/15 text-softgold'
                    : 'bg-navy/5 text-navy/50'
                }`}
              >
                {card.tag}
              </span>

              <h3
                className={`mb-2 text-[15px] font-semibold ${
                  card.highlight ? 'text-cream' : 'text-navy'
                }`}
              >
                {card.title}
              </h3>

              <p
                className={`text-[12px] leading-relaxed ${
                  card.highlight ? 'text-cream/60' : 'text-navy/50'
                }`}
              >
                {card.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}